import {
  IconBuildingSkyscraper,
  IconCheck,
  IconSearch,
  IconSelector,
  IconWorld,
} from '@tabler/icons-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import TenantBadge from 'components/common/TenantBadge';
import { useAuth } from 'hooks/useAuth';
import { useOnClickOutside } from 'hooks/useOnClickOutside';
import { authService } from 'services/authService';
import { isMasterRole, isSuperAdminRole } from 'util/access';
import { toastError } from 'util/toast';

/**
 * Super-admin "Switch Center" dropdown (header, next to the user menu).
 *
 * Lists every center the logged-in super admin (or master) is assigned to and
 * switches the whole application context to the picked one via
 * AuthContext.switchCenter → POST /auth/switch-context (which re-mints a
 * center-scoped token and reloads, so all modules show only that center's data).
 *
 * Rendering rules (per the spec):
 *   - only for organization super admins — nothing for other roles, and not for
 *     the platform master (whose token is always platform-scoped, so a center
 *     switch is a backend no-op);
 *   - hidden entirely when the user has a single (or no) switchable center;
 *   - shows the currently-active center, with "All centers" (org scope) as the
 *     default option and the active choice check-marked.
 */
export function CenterSwitcher() {
  const { role, switchCenter } = useAuth();
  const isSuperAdmin = isSuperAdminRole(role) && !isMasterRole(role);

  const [centers, setCenters] = useState([]);
  const [currentCenterId, setCurrentCenterId] = useState(null);
  const [organizationId, setOrganizationId] = useState(null);
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef(null);

  useOnClickOutside(ref, () => setOpen(false));

  useEffect(() => {
    if (!isSuperAdmin) return undefined;
    let active = true;
    authService
      .getSwitchCenters()
      .then((response) => {
        if (!active) return;
        const data = response?.data || response || {};
        setCenters(Array.isArray(data.centers) ? data.centers : []);
        setCurrentCenterId(data.current_center_id ?? null);
        setOrganizationId(data.organization_id ?? null);
      })
      .catch(() => {
        /* leave the switcher hidden if the list can't be loaded */
      });
    return () => {
      active = false;
    };
  }, [isSuperAdmin]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return centers;
    return centers.filter((c) =>
      [c.name, c.location, c.admin_name].filter(Boolean).join(' ').toLowerCase().includes(q),
    );
  }, [centers, query]);

  // Only super admins with more than one assigned center get a switcher.
  if (!isSuperAdmin || centers.length <= 1) return null;

  const current = centers.find((c) => String(c.id) === String(currentCenterId));
  const currentLabel = current ? current.name : 'All centers';

  const toggle = () =>
    setOpen((v) => {
      if (v) setQuery('');
      return !v;
    });

  // `center` is a center object, or null for "All centers" (organization scope).
  const choose = async (center) => {
    const targetId = center ? center.id : null;
    // No-op if the picked option is already active.
    if (String(targetId ?? '') === String(currentCenterId ?? '')) {
      setOpen(false);
      return;
    }
    setSwitching(true);
    try {
      // Triggers a full reload on success; the catch below only runs on error.
      await switchCenter(
        center ? { center_id: center.id } : { organization_id: organizationId },
      );
    } catch (err) {
      toastError(err?.message || 'Could not switch center.');
      setSwitching(false);
      setOpen(false);
    }
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={toggle}
        disabled={switching}
        title="Switch center"
        aria-haspopup="menu"
        aria-expanded={open}
        className={`flex h-[38px] max-w-[210px] items-center gap-2 rounded-full border bg-surface py-1 pl-1 pr-2.5 transition-colors disabled:opacity-60 ${
          open ? 'border-brand/50 ring-2 ring-brand/15' : 'border-hair hover:bg-mist'
        }`}
      >
        {current ? (
          <TenantBadge
            name={currentLabel}
            logoUrl={current.logo_url}
            size={26}
            className="text-[11px]"
          />
        ) : (
          <span className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-full bg-mist text-slate">
            <IconWorld className="h-4 w-4" />
          </span>
        )}
        <span className="hidden min-w-0 truncate text-sm font-medium text-ink sm:block">
          {switching ? 'Switching…' : currentLabel}
        </span>
        <IconSelector className="h-4 w-4 shrink-0 text-slate" />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 z-30 mt-2 w-72 overflow-hidden rounded-2xl border border-hair bg-surface shadow-[0_18px_50px_-12px_rgba(20,20,30,.28)]"
        >
          {/* Header */}
          <div className="flex items-center justify-between gap-2 border-b border-hair px-4 py-3">
            <div className="min-w-0">
              <p className="text-[13px] font-semibold text-ink">Switch center</p>
              <p className="text-[11px] text-slate">{centers.length} centers available</p>
            </div>
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand/10 text-brand-dark">
              <IconBuildingSkyscraper className="h-[18px] w-[18px]" />
            </span>
          </div>

          {/* Search */}
          <div className="px-3 pt-2.5">
            <div className="flex items-center gap-2 rounded-lg bg-mist px-3 py-2">
              <IconSearch className="h-4 w-4 shrink-0 text-silver" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search centers…"
                className="w-full bg-transparent text-sm text-ink outline-none placeholder:text-silver"
              />
            </div>
          </div>

          {/* Options */}
          <div className="max-h-[56vh] overflow-auto p-1.5">
            <MenuRow
              badge={
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-mist text-slate">
                  <IconWorld className="h-[18px] w-[18px]" />
                </span>
              }
              label="All centers"
              sub="Organization-wide view"
              active={!currentCenterId}
              onClick={() => choose(null)}
            />
            <div className="mx-2 my-1 border-t border-hair" />
            {filtered.map((c) => (
              <MenuRow
                key={c.id}
                badge={
                  <TenantBadge
                    name={c.name}
                    logoUrl={c.logo_url}
                    size={32}
                    className="text-[12px]"
                  />
                }
                label={c.name}
                sub={[c.location, c.admin_name].filter(Boolean).join(' · ')}
                active={String(c.id) === String(currentCenterId)}
                onClick={() => choose(c)}
              />
            ))}
            {filtered.length === 0 && (
              <p className="px-3 py-6 text-center text-sm text-slate">No centers found.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function MenuRow({ badge, label, sub, active, onClick }) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className={`flex w-full items-center gap-3 rounded-xl px-2.5 py-2 text-left transition-colors ${
        active ? 'bg-brand/[0.07]' : 'hover:bg-mist'
      }`}
    >
      {badge}
      <span className="min-w-0 flex-1">
        <span className={`block truncate text-sm ${active ? 'font-semibold text-ink' : 'font-medium text-ink'}`}>
          {label}
        </span>
        {sub && <span className="block truncate text-xs text-slate">{sub}</span>}
      </span>
      {active && <IconCheck className="h-[18px] w-[18px] shrink-0 text-brand-dark" />}
    </button>
  );
}

export default CenterSwitcher;
